




































































import Vue, { PropType } from 'vue';
import { ConfirmationModal, MESSAGE_SIZE_LIMIT, EventType } from '@/constants';
import AttachIcon from '@/assets/icons/AttachIcon.vue';
import CloseIcon from '@/assets/icons/CloseIcon.vue';
import InfoIcon from '@/assets/icons/InfoIcon.vue';
import SendIcon from '@/assets/icons/SendIcon.vue';
import EditSubjectIcon from '@/assets/icons/EditSubjectIcon.vue';
import CKEditorWrapper from '@/views/appointments/components/CKEditorWrapper.vue';
import { AttachmentUrls, ConfirmationModalData } from '@/types';
import { bus } from '@/main';
import { isIOS } from '@/util/funcs';

export default Vue.extend({
  name: 'MessageComposePanel',
  components: {
    AttachIcon,
    CloseIcon,
    InfoIcon,
    SendIcon,
    EditSubjectIcon,
    CKEditorWrapper,
    ConfirmationModal: () => import('@/components/modals/ConfirmationModal.vue')
  },
  data () {
    return {
      message: '',
      messageSubject: '',
      basePanelHeight: 0,
      showCharacterLimitIndicator: false,
      messagePlaceholder: 'Write a message or attach a file',
      subjectPlaceholder: 'Add a subject (Optional)',
      removeAttachment: false,
      cancelMessage: false,
      modalData: {} as ConfirmationModalData,
      fileToRemove: {} as AttachmentUrls,
      editorKey: new Date().getTime().toString(),
      textEditorMinimized: true,
      EventType,
      isIOS,
      dynamicRows: 1,
      initial: true
    }
  },
  props: {
    recipientSelectionError: {
      type: String,
      required: true
    },
    uploadedFiles: {
      type: Array as PropType<AttachmentUrls[]>,
      default: []
    },
    isThreadOpen: {
      type: Boolean,
      required: true
    },
    activeConversation: {
      type: String,
      required: true
    },
    lastMessageSubject: {
      type: String,
      default: ''
    },
    isLoggedInAsAdmin: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    characterLimitExceeded (): boolean {
      return this.message.length > MESSAGE_SIZE_LIMIT;
    },
    updateRows (): number {
      return this.dynamicRows;
    },
    getMessagePlaceholder () {
      if (!this.initial) {
        return '';
      }
      return this.isLoggedInAsAdmin ? 'This conversation has been closed. You may create a New Message with active Drees recipients.' : (this as any).messagePlaceholder;
    }
  },
  watch: {
    characterLimitExceeded (value) {
      if (value) {
        this.showCharacterLimitIndicator = true;
      } else {
        this.showCharacterLimitIndicator = false;
      }
    },
    activeConversation () {
      this.resetData();
    },
    lastMessageSubject () {
      this.messageSubject = this.lastMessageSubject
    },
    message (value) {
      if (!value && this.$refs.composePanel) {
        (this.$refs.composePanel as HTMLElement).style.minHeight = this.basePanelHeight + 'px';
      } else if (!value && this.$refs.previewPanel) {
        (this.$refs.previewPanel as HTMLElement).style.minHeight = this.basePanelHeight + 'px';
      }
      if (value.length === 0) {
        this.dynamicRows = 1;
      }
      this.initial = !value.length;
    }
  },
  mounted () {
    if (this.$refs.composePanel) {
      this.basePanelHeight = (this.$refs.composePanel as HTMLElement).clientHeight || 0;
    } else if (this.$refs.previewPanel) {
      this.basePanelHeight = (this.$refs.previewPanel as HTMLElement).clientHeight || 0;
    }
    this.$parent.$on('resetMessageData', this.resetData);
    if (document.querySelector('.compose-panel')) {
      (document.querySelector('.compose-panel') as HTMLElement).addEventListener('click', async (evt: any) => {
        const myTimeOut = setTimeout(() => {
          (document.querySelector('.compose-panel') as HTMLTextAreaElement).focus();
          clearTimeout(myTimeOut);
        }, 100);
      });
    }
    if (this.lastMessageSubject) {
      this.messageSubject = this.lastMessageSubject
    }
    // Listen for clicks outside the editor to un-minimize
    document.addEventListener('click', this.onGlobalClick, true);
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onGlobalClick, true);
  },
  methods: {
    onGlobalClick (evt: MouseEvent) {
      const container = (this.$refs as any).editorContainerRef as HTMLElement | undefined;
      const target = evt.target as Node | null;
      if (container && target && !container.contains(target)) {
        this.textEditorMinimized = true;
      }
    },
    updateMessageData (data: string) {
      this.message = data;
    },
    calculateRows () {
      const text = this.message;
      const lineByEnterStrokes = text.split(/\r?\n/);
      let currentLines = 0;
      lineByEnterStrokes.forEach((line, idx) => {
        if (!line.length) currentLines += 1;
        currentLines += this.calculateWrappedLines(line);
      });
      this.dynamicRows = Math.min(currentLines || 1, 10);
    },
    calculateWrappedLines (line: string): number {
      const textAreaCompose = (this.$refs.composePanel as HTMLTextAreaElement);
      const textAreaPreview = (this.$refs.previewPanel as HTMLTextAreaElement);
      if (!textAreaCompose && !textAreaPreview) {
        // console.error('composeText ref is not defined');
        return 98;
      }
      const textArea = textAreaCompose || textAreaPreview;
      const style = window.getComputedStyle(textArea);
      const width = parseInt(style.width, 10);
      const padding = parseInt(style.paddingLeft, 10) + parseInt(style.paddingRight, 10);
      const border = parseInt(style.borderLeftWidth, 10) + parseInt(style.borderRightWidth, 10);
      const usableWidth = width - padding - border;
      const lineWidth = this.getTextWidth(line, style.font);
      return Math.ceil(lineWidth / usableWidth);
    },
    getTextWidth (text: string, font: string): number {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        context.font = font;
        return context.measureText(text).width;
      }
      return 0;
    },
    checkState () {
      return !this.message.length;
    },
    customEventTrigger () {
      this.initial = false;
      (document.querySelector('.compose-panel') as HTMLElement).click();
    },
    async sendMessage () {
      if (this.message.trim() || !this.characterLimitExceeded) {
        await this.$emit(EventType.SEND_MESSAGE, {
          message: this.message,
          messageSubject: this.messageSubject
        });
        this.initial = true;
      }
    },
    handleCancelMessage () {
      if (!this.isLoggedInAsAdmin) {
        this.cancelMessage = true;
        this.initial = true;
        this.modalData = ConfirmationModal.CancelMessage;
      } else {
        bus.$emit(EventType.VIEW_VERSION);
      }
    },
    closeConfirmationModal () {
      this.modalData = {} as ConfirmationModalData;
      this.initial = true;
      this.cancelMessage = false;
      this.removeAttachment = false;
      this.fileToRemove = {} as AttachmentUrls;
    },
    deleteAttachment (file: AttachmentUrls) {
      if (!this.isLoggedInAsAdmin) {
        this.removeAttachment = true;
        this.modalData = ConfirmationModal.RemoveAttachment;
        this.fileToRemove = file;
      } else {
        bus.$emit(EventType.VIEW_VERSION);
      }
    },
    getNamefromAttachmentUrl (url: string) {
      let fileName: string = url;
      if (url) {
        const urlArray = url.split('/');
        fileName = urlArray[urlArray.length - 1];
        const index = fileName.indexOf('-');
        fileName = fileName.substring(index + 1);
      }
      return fileName;
    },
    handleConfirmAction () {
      if (this.cancelMessage) {
        this.resetData();
        this.message = '';
        this.editorKey = new Date().getTime().toString();
        this.cancelMessage = false;
        if (this.uploadedFiles.length) {
          this.$emit(EventType.DELETE_ALL_FILES, this.uploadedFiles);
        }
      } else {
        this.$emit(EventType.DELETE_ATTACHMENT, {
          fileName: this.fileToRemove.fileName,
          id: this.fileToRemove.id
        });
        this.closeConfirmationModal();
      }
    },
    resetData () {
      this.message = '';
      this.editorKey = new Date().getTime().toString();
      this.messageSubject = this.lastMessageSubject;
      this.initial = true;
    }
  }
});
